import Vue from 'vue'
import VueRouter from 'vue-router'
import { axiosCredentials } from '@/axios-credentials'
import store from '@/store'
import VueGtag from 'vue-gtag'
import logger from '@/utils/logger'

Vue.use(VueRouter)

// isPublic:tureならログイン・アカウント登録なしでアクセス可能
// registerNotRequired:tureならアカウント登録前にアクセス可能（ログイン要）
const routes = [
  {
    path: '/',
    name: 'root',
    meta: { isPublic: true },
    beforeEnter(to, from, next) {
      // 有効なsessionを持っていればhomeへ、なければloginへ
      axiosCredentials
        .get('/ui_api/user/name')
        .then((response) => {
          logger.debug(response)
          store.dispatch('setUserInfo', {
            surname: response.data.surname,
            firstname: response.data.firstname,
            registered: response.data.registered,
          })
          next('/home')
        })
        .catch((error) => {
          logger.error(error)
          store.dispatch('clearUserInfo')
          next('/login')
        })
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { isPublic: true },
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/Home.vue'),
    meta: { pageTitle: 'MYPLR口座 にようこそ' },
  },
  {
    // プライバシーポリシー/利用規約
    path: '/privacy-and-terms',
    name: 'privacy-and-terms',
    component: () => import('@/views/PrivacyAndTerms.vue'),
    meta: { prevPage: 'home', isPublic: true },
    beforeEnter(to, from, next) {
      // ヘッダーのボタンによる遷移時のナビゲーションガード回避のため、ヘッダーの表示・非表示を切り替える
      if (store.getters.isLoggedIn) {
        to.meta.pageTitle = 'プライバシーポリシー';
      }
      next();
    }
  },
  {
    // プライバシーポリシー/利用規約（同意）
    path: '/privacy-and-terms/consent/:authenticationMethod',
    name: 'privacy-and-terms-consent',
    component: () => import('@/views/PrivacyAndTermsConsent.vue'),
    props: (route) => ({ authenticationMethod: String(route.params.authenticationMethod) }),
    meta: { prevPage: 'register-top', isPublic: true  },
  },
  {
    // アカウント
    path: '/account',
    name: 'account-menu',
    component: () => import('@/views/AccountMenu.vue'),
    meta: { pageTitle: 'アカウント', prevPage: 'home' },
  },
  {
    // 外部アカウント連携
    path: '/account/openid-providers',
    name: 'openid-providers',
    component: () => import('@/views/OpenIDProviderList.vue'),
    meta: { pageTitle: '外部サービス連携', prevPage: 'account-menu' },
  },
  {
    // 外部アカウント連携解除+パスワード設定
    path: '/account/openid-providers/:providerCode/disconnect-with-password',
    name: 'openid-provider-disconnect',
    component: () => import('@/views/OpenIDProviderDisconnect.vue'),
    props: true,
    meta: { pageTitle: '外部サービス連携' },
    beforeEnter(to, from, next) {
      if (from.name === 'openid-providers') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // アカウント登録トップ
    path: '/account/register-top',
    name: 'register-top',
    component: () => import('@/views/RegistrerTop.vue'),
    meta: { isPublic: true },
  },
  {
    // アカウント登録（パスワード方式）新規登録
    path: '/account/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: { isPublic: true },
  },
  {
    // アカウント登録（パスワード方式）確認メール送信メッセージ
    path: '/account/register/email-sent',
    name: 'register-email-sent',
    component: () => import('@/views/RegisterEmailSent.vue'),
    meta: { isPublic: true },
    beforeEnter(to, from, next) {
      if (from.name === 'register') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // アカウント登録（パスワード方式）メールアドレス確認・登録完了
    path: '/account/register/completed/:userId',
    name: 'register-completed',
    component: () => import('@/views/RegisterCompleted.vue'),
    meta: { isPublic: true },
  },
  {
    // アカウント登録（外部連携方式）ユーザ情報更新 ＊ログイン要、アカウント登録不要
    path: '/account/register-openid',
    name: 'register-openid',
    component: () => import('@/views/RegisterOpenID.vue'),
    meta: { registerNotRequired: true },
  },
  {
    // アカウント登録（外部連携方式）ユーザ情報更新完了
    path: '/account/register-openid/completed',
    name: 'register-openid-completed',
    component: () => import('@/views/RegisterOpenIDCompleted.vue'),
    beforeEnter(to, from, next) {
      if (from.name === 'register-openid') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // パスワード再設定URLリクエスト
    path: '/account/password-reset-request',
    name: 'password-reset-request',
    component: () => import('@/views/PasswordResetRequest.vue'),
    meta: { isPublic: true },
  },
  {
    // パスワード再設定URLリクエスト完了
    path: '/account/password-reset-request/completed',
    name: 'password-request-completed',
    component: () => import('@/views/PasswordResetRequestCompleted.vue'),
    meta: { isPublic: true },
    beforeEnter(to, from, next) {
      if (from.name === 'password-reset-request') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // パスワード再設定完了
    path: '/account/password-reset/completed',
    name: 'password-reset-completed',
    component: () => import('@/views/PasswordResetCompleted.vue'),
    meta: { isPublic: true },
    beforeEnter(to, from, next) {
      if (from.name === 'password-reset') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // パスワード再設定
    path: '/account/password-reset/:userId',
    name: 'password-reset',
    component: () => import('@/views/PasswordReset.vue'),
    meta: { isPublic: true },
  },
  {
    // 初期パスワード変更
    path: '/account/password-change-init',
    name: 'password-change-init',
    component: () => import('@/views/PasswordChangeInit.vue'),
  },
  {
    // 初期パスワード変更完了
    path: '/account/password-change-init/completed',
    name: 'password-change-init-completed',
    component: () => import('@/views/PasswordChangeInitCompleted.vue'),
    beforeEnter(to, from, next) {
      if (from.name === 'password-change-init') {
        next()
      } else {
        next('/login')
      }
    },
  },
  {
    // 通知一覧
    path: '/notification',
    name: 'notification',
    component: () => import('@/views/NotificationList.vue'),
    meta: { pageTitle: '通知一覧', prevPage: 'home' },
  },
  {
    // プロジェクトトップ
    path: '/project',
    name: 'project-top',
    component: () => import('@/views/ProjectTop.vue'),
    meta: { pageTitle: 'プロジェクト登録', prevPage: 'home' },
  },
  {
    // プロジェクト一覧
    path: '/project/list',
    name: 'project-list',
    component: () => import('@/views/ProjectList.vue'),
    meta: { pageTitle: '登録済プロジェクト', prevPage: 'project-top' },
  },
  {
    // プロジェクト詳細
    path: '/project/detail/:projectCode',
    name: 'project-detail',
    component: () => import('@/views/ProjectDetail.vue'),
    props: (route) => ({ projectCode: Number(route.params.projectCode) }),
    meta: { pageTitle: 'プロジェクト詳細' },
  },
  {
    // 医療情報連携同意
    path: '/project/medical-consent/:projectCode',
    name: 'project-medical-consent',
    component: () => import('@/views/ProjectMedicalConsent.vue'),
    props: (route) => ({ projectCode: Number(route.params.projectCode) }),
    meta: { pageTitle: '医療情報に関する同意' }
  },
  {
    // プロジェクト登録完了
    path: '/project/linkage/completed',
    name: 'project-linkage-completed',
    component: () => import('@/views/ProjectCompleted.vue'),
    meta: { pageTitle: 'プロジェクト登録申請完了' },
    beforeEnter(to, from, next) {
      if (
        (from.name === 'project-detail' || from.name === 'project-consent-guide' || from.name === 'project-medical-consent') &&
        store.state.project.projectCode
      ) {
        next()
      } else {
        next('/home')
      }
    },
  },
  {
    // プロジェクト同意設定説明
    path: '/project/consent-type/guide',
    name: 'project-consent-guide',
    component: () => import('@/views/ProjectConsentGuide.vue'),
    meta: { pageTitle: 'データ提供の同意 1/3' },
    beforeEnter(to, from, next) {
      if (
        (from.name === 'project-linkage-completed' ||
          from.name === 'project-consent-setting' ||
          from.name === 'project-detail') &&
        store.state.project.projectCode
      ) {
        next()
      } else {
        next('/home')
      }
    },
  },
  {
    // プロジェクト同意設定
    path: '/project/consent-type/setting',
    name: 'project-consent-setting',
    component: () => import('@/views/ProjectConsentSetting.vue'),
    meta: { pageTitle: 'データ提供の同意 2/3' },
    beforeEnter(to, from, next) {
      if (
        (from.name === 'project-consent-guide' || from.name === 'project-consent-completed') &&
        store.state.project.projectCode
      ) {
        next()
      } else {
        next('/home')
      }
    },
  },
  {
    // プロジェクト同意設定完了
    path: '/project/consent-type/completed',
    name: 'project-consent-completed',
    component: () => import('@/views/ProjectConsentCompleted.vue'),
    props: true,
    meta: { pageTitle: 'データ提供の同意 3/3' },
    beforeEnter(to, from, next) {
      if (from.name === 'project-consent-setting' && store.state.project.projectCode) {
        next()
      } else {
        next('/home')
      }
    },
  },
  {
    // データ提供契約募集中一覧
    path: '/contract/recruiting-list',
    name: 'contract-recruiting',
    component: () => import('@/views/ContractRecruitingList.vue'),
    meta: { pageTitle: '募集中データ提供契約一覧', prevPage: 'home' },
  },
  {
    // データ提供契約詳細
    path: '/contract/detail/:type(1|2)/:contractId',
    name: 'contract-detail',
    component: () => import('@/views/ContractDetail.vue'),
    props: (route) => ({
      type: Number(route.params.type),
      contractId: Number(route.params.contractId),
    }),
    meta: { pageTitle: 'データ提供契約詳細' },
  },
  {
    // データ提供契約回答完了
    path: '/contract/consent/completed',
    name: 'contract-consent-completed',
    component: () => import('@/views/ContractConsentCompleted.vue'),
    props: true,
    meta: { pageTitle: 'データ提供契約回答完了' },
    beforeEnter(to, from, next) {
      if (from.name === 'contract-detail') {
        next()
      } else {
        next('/home')
      }
    },
  },
  {
    // すべてのデータ提供契約一覧
    path: '/contract/all-list',
    name: 'contract-all',
    component: () => import('@/views/ContractAllList.vue'),
    meta: { pageTitle: 'すべてのデータ募集', prevPage: 'home' },
  },
  {
    // 取引履歴
    path: '/transaction-history',
    name: 'transaction-history',
    component: () => import('@/views/TransactionHistory.vue'),
    meta: { pageTitle: 'データ取引状況', prevPage: 'home' },
  },
  {
    // 蓄積履歴
    path: '/accumulation-history',
    name: 'accumulation-history',
    component: () => import('@/views/AccumulationHistory.vue'),
    meta: { pageTitle: 'データ蓄積状況', prevPage: 'home' },
  },
  {
    // セッション切れ
    path: '/session-expired',
    name: 'session-expired',
    component: () => import('@/views/SessionExpired.vue'),
    meta: { isPublic: true },
  },
  {
    // 高齢者施設向けタブレットポータル
    path: '/room_number',
    name: 'room-number-portal',
    component: () => import('@/views/RoomNumberPortal.vue'),
    meta: { isPublic: true },
  },
  {
    // 上のいずれともマッチしなかった場合
    path: '*',
    redirect: '/login',
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

export default router

// ページ遷移前に未ログインとアカウント未登録をチェックする
router.beforeEach((to, from, next) => {
  if (to.meta.isPublic) {
    // 公開ページはそのまま遷移
    next()
  } else {
    if (!store.getters.isLoggedIn) {
      // ログインされてない場合はログインページへ遷移
      next('/login')
    } else if (to.meta.registerNotRequired || store.getters.isRegistered) {
      // ログイン済で、アカウント登録済またはアカウント登録不要のページはそのまま遷移
      next()
    } else {
      // アカウント登録されていない場合は登録ページへ遷移
      next('/account/register-openid')
    }
  }
})

Vue.use(
  VueGtag,
  {
    config: { id: process.env.VUE_APP_GOOGLE_ANALYTICS_ID },
  },
  router
)
